import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";

import {
  MapPin,
  CalendarDays,
  Clock,
  Gift,
  Tag,
  Utensils,
  Truck,
  Banknote,
  Navigation,
  LocateFixed,
  Loader2,
  RefreshCw,
} from "lucide-react";


// ============================================================
// CONSTANTS
// ============================================================

const ORDER_TYPES = {
  DINE_IN: "DINE_IN",
  DELIVERY: "DELIVERY",
};

const DELIVERY_TIMES = [
  "ASAP",
  "30 Minutes",
  "1 Hour",
  "Schedule",
];

const TABLES = Array.from(
  { length: 10 },
  (_, index) => `Table ${index + 1}`
);

// ============================================================
// DISTANCE
// ============================================================

function calculateDistanceKm(
  lat1,
  lon1,
  lat2,
  lon2
) {
  const toRadians = (value) =>
    (value * Math.PI) / 180;

  const earthRadiusKm = 6371;

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c =
    2 *
    Math.atan2(
      Math.sqrt(a),
      Math.sqrt(1 - a)
    );

  return earthRadiusKm * c;
}

// ============================================================
// NEAREST SHOP
// ============================================================

function findNearestShop(
  shops,
  latitude,
  longitude
) {
  if (!Array.isArray(shops) || shops.length === 0) {
    return null;
  }

  const validShops = shops
    .map((shop) => {
      const shopLat = Number(
        shop.latitude ??
          shop.lat ??
          shop.location?.latitude
      );

      const shopLng = Number(
        shop.longitude ??
          shop.lng ??
          shop.lon ??
          shop.location?.longitude
      );

      if (
        !Number.isFinite(shopLat) ||
        !Number.isFinite(shopLng)
      ) {
        return null;
      }

      const distance =
        calculateDistanceKm(
          latitude,
          longitude,
          shopLat,
          shopLng
        );

      return {
        ...shop,
        latitude: shopLat,
        longitude: shopLng,
        distance,
      };
    })
    .filter(Boolean);

  if (validShops.length === 0) {
    return null;
  }

  return validShops.sort(
    (a, b) =>
      a.distance - b.distance
  )[0];
}

// ============================================================
// BROWSER GPS
// ============================================================
function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(
        new Error(
          "Geolocation is not supported by this browser."
        )
      );
      return;
    }

    let bestPosition = null;
    let watchId = null;

    const cleanup = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };

    const finish = (position) => {
      cleanup();
      resolve(position);
    };

    const fail = (error) => {
      cleanup();
      reject(error);
    };

    // First request: force a fresh high-accuracy reading.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const accuracy = Number(
          position.coords.accuracy
        );

        console.log(
          "Initial GPS:",
          position.coords.latitude,
          position.coords.longitude,
          "accuracy:",
          accuracy,
          "meters"
        );

        // Accept immediately if reasonably accurate.
        if (
          Number.isFinite(accuracy) &&
          accuracy <= 500
        ) {
          finish(position);
          return;
        }

        // If the browser gave a poor result,
        // continue watching for a better GPS fix.
        bestPosition = position;

        watchId = navigator.geolocation.watchPosition(
          (newPosition) => {
            const newAccuracy = Number(
              newPosition.coords.accuracy
            );

            console.log(
              "GPS update:",
              newPosition.coords.latitude,
              newPosition.coords.longitude,
              "accuracy:",
              newAccuracy,
              "meters"
            );

            if (
              !bestPosition ||
              newAccuracy <
                Number(
                  bestPosition.coords.accuracy
                )
            ) {
              bestPosition = newPosition;
            }

            // Excellent / usable GPS.
            if (
              Number.isFinite(newAccuracy) &&
              newAccuracy <= 500
            ) {
              finish(newPosition);
            }
          },
          fail,
          {
            enableHighAccuracy: true,
            timeout: 60000,
            maximumAge: 0,
          }
        );

        // Don't wait forever.
        setTimeout(() => {
          if (bestPosition) {
            const accuracy = Number(
              bestPosition.coords.accuracy
            );

            if (accuracy <= 5000) {
              finish(bestPosition);
            } else {
              fail(
                new Error(
                  `GPS accuracy is too low (${Math.round(
                    accuracy
                  )} meters). Please turn on Windows Location Services and try again.`
                )
              );
            }
          } else {
            fail(
              new Error(
                "Unable to obtain a GPS location."
              )
            );
          }
        }, 60000);
      },
      fail,
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      }
    );
  });
}

// ============================================================
// REVERSE GEOCODING
// ============================================================

async function reverseGeocode(
  latitude,
  longitude
) {
  const url =
    "https://nominatim.openstreetmap.org/reverse" +
    `?format=jsonv2` +
    `&lat=${encodeURIComponent(latitude)}` +
    `&lon=${encodeURIComponent(longitude)}` +
    `&zoom=18` +
    `&addressdetails=1`;
    `&accept-language=en`;

  const response = await fetch(url, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(
      "Unable to detect your address."
    );
  }

  const data = await response.json();

  const address = data.address || {};

  const parts = [
      address.house_number,
      address.road,
      address.neighbourhood,
      address.suburb,
      address.city_district,
      address.town,
      address.village,
      address.district,
      address.state_district,
      address.state,
      address.postcode,
      address.country,
    ].filter(Boolean);
    
    const uniqueParts = [...new Set(parts)];

    const readableAddress = uniqueParts.join(", ");
    

  return {
    address:
      readableAddress ||
      `Current location (${latitude.toFixed(
        6
      )}, ${longitude.toFixed(6)})`,
    pincode: address.postcode || "",
  };
}

// ============================================================
// FIELD
// ============================================================

function Field({
  label,
  value,
  onChange,
  testId,
  type = "text",
  placeholder = "",
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#B7D8D5] mb-2">
        {label}
      </label>

      <input
        data-testid={testId}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value)
        }
        className="w-full rounded-2xl border border-[rgba(184,225,74,0.35)] bg-white text-[#1F5F5C] px-5 py-3 outline-none focus:ring-2 focus:ring-[#B8E14A]"
      />
    </div>
  );
}

// ============================================================
// PAYMENT BUTTON
// ============================================================

function PaymentButton({
  active,
  onClick,
  icon,
  title,
  subtitle,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-5 rounded-2xl border-2 text-left transition ${
        active
          ? "border-[#B8E14A] bg-[#B8E14A]/10"
          : "border-[rgba(184,225,74,0.25)]"
      }`}
    >
      {icon}

      <div className="font-medium mt-2">
        {title}
      </div>

      <div className="text-xs text-[#B7D8D5] mt-1">
        {subtitle}
      </div>

      {active && (
        <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
          ✓ Selected
        </div>
      )}
    </button>
  );
}

// ============================================================
// PRICE ROW
// ============================================================

function Row({
  label,
  value,
  accent = false,
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#E6F4F2]">
        {label}
      </span>

      <span
        className={
          accent
            ? "text-green-700 font-semibold"
            : "font-medium"
        }
      >
        {value}
      </span>
    </div>
  );
}
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}
// ============================================================
// CHECKOUT
// ============================================================

export default function Checkout() {
  const {
    items,
    subtotal,
    clear,
  } = useCart();

  const nav = useNavigate();

  // ----------------------------------------------------------
  // SETTINGS
  // ----------------------------------------------------------

  const [settings, setSettings] =
    useState(null);

  // ----------------------------------------------------------
  // SHOPS
  // ----------------------------------------------------------

  const [shops, setShops] =
    useState([]);

  const [shopsLoading, setShopsLoading] =
    useState(false);

  // ----------------------------------------------------------
  // CART PRICING
  // ----------------------------------------------------------

  const [cartPricing, setCartPricing] =
    useState(null);

  // ----------------------------------------------------------
  // ORDER TYPE
  // ----------------------------------------------------------

  const [orderType, setOrderType] =
    useState("");

  // ----------------------------------------------------------
  // CUSTOMER
  // ----------------------------------------------------------

  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    landmark: "",
    pincode: "",
    map_link: "",
    delivery_time: "ASAP",
  });

  // ----------------------------------------------------------
  // DINE-IN
  // ----------------------------------------------------------

  const [
    selectedDineShop,
    setSelectedDineShop,
  ] = useState(null);

  const [tableNo, setTableNo] =
    useState("");

  const [
    dinePaymentMethod,
    setDinePaymentMethod,
  ] = useState("CASH");

  // ----------------------------------------------------------
  // DELIVERY GPS
  // ----------------------------------------------------------

  const [
    deliveryLocation,
    setDeliveryLocation,
  ] = useState(null);

  const [
    selectedShop,
    setSelectedShop,
  ] = useState(null);

  const [
    shopDistance,
    setShopDistance,
  ] = useState(null);

  const [
    locationLoading,
    setLocationLoading,
  ] = useState(false);

  const [
    locationError,
    setLocationError,
  ] = useState("");

  // ----------------------------------------------------------
  // SCHEDULE
  // ----------------------------------------------------------

  const [
    scheduledDate,
    setScheduledDate,
  ] = useState("");

  const [
    scheduledTime,
    setScheduledTime,
  ] = useState("");

  // ----------------------------------------------------------
  // ORDER
  // ----------------------------------------------------------

  const [placing, setPlacing] =
    useState(false);
  const [paymentSuccess, setPaymentSuccess] =
  useState(null);
  // ==========================================================
  // LOAD SETTINGS
  // ==========================================================

  useEffect(() => {
    api
      .get("/settings")
      .then((response) => {
        setSettings(response.data);
      })
      .catch((error) => {
        console.error(
          "Settings error:",
          error
        );
      });
  }, []);

  // ==========================================================
  // LOAD SHOPS FROM BACKEND
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const loadShops = async () => {
      setShopsLoading(true);

      try {
        const response =
          await api.get("/shops");

        const data =
          Array.isArray(response.data)
            ? response.data
            : response.data?.shops || [];

        if (!cancelled) {
          setShops(data);
        }
      } catch (error) {
        console.error(
          "Unable to load shops:",
          error
        );

        if (!cancelled) {
          toast.error(
            "Unable to load shops."
          );
        }
      } finally {
        if (!cancelled) {
          setShopsLoading(false);
        }
      }
    };

    loadShops();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================================
  // LOAD CART PRICING
  // ==========================================================

  useEffect(() => {
    try {
      const saved =
        sessionStorage.getItem(
          "icp_cart_pricing"
        );

      if (!saved) {
        setCartPricing(null);
        return;
      }

      const parsed =
        JSON.parse(saved);

      if (
        Number(parsed.subtotal) !==
        Number(subtotal)
      ) {
        sessionStorage.removeItem(
          "icp_cart_pricing"
        );

        setCartPricing(null);
        return;
      }

      setCartPricing(parsed);
    } catch (error) {
      console.error(
        "Cart pricing error:",
        error
      );

      setCartPricing(null);
    }
  }, [subtotal]);

  // ==========================================================
  // PRICING
  // ==========================================================

  const gstPct =
    settings?.gst_percent ?? 5;

  const freeDeliveryAbove =
    settings?.free_delivery_above ??
    499;

  const normalDeliveryCharge =
    settings?.delivery_charge ??
    40;

  const discount = Math.max(
    0,
    Number(
      cartPricing?.discount ?? 0
    )
  );

  const couponCode =
    cartPricing?.coupon_code ||
    null;

  const offerId =
    cartPricing?.offer_id ||
    null;

  const offerTitle =
    cartPricing?.offer_title ||
    null;

  const offerDiscount =
    Number(
      cartPricing?.offer_discount ??
        0
    );

  const deliveryCharge =
    orderType === ORDER_TYPES.DINE_IN
      ? 0
      : Number(subtotal) >=
        Number(freeDeliveryAbove)
      ? 0
      : Number(
          normalDeliveryCharge
        );

  const afterDiscount =
    Math.max(
      0,
      Number(subtotal) -
        discount
    );

  const gst = Math.round(
    (afterDiscount * gstPct) /
      100
  );

  const total = Math.max(
    0,
    afterDiscount +
      gst +
      deliveryCharge
  );

  // ==========================================================
  // TODAY
  // ==========================================================

  const getToday = () => {
    const date = new Date();

    const year =
      date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ==========================================================
  // CURRENT GPS LOCATION
  //
  // IMPORTANT:
  // This function is called ONLY when the user clicks
  // "Use My Current Location".
  // ==========================================================

  const fetchCurrentLocation =
    async () => {
      if (locationLoading) {
        return;
      }

      setLocationLoading(true);
      setLocationError("");

      toast.loading(
        "Getting your current location...",
        {
          id: "location-loading",
        }
      );

      try {
        // ----------------------------------------------------
        // GET FRESH GPS POSITION
        // ----------------------------------------------------

        const position =
          await getCurrentPosition();

        const latitude =
          Number(
            position.coords.latitude
          );

        const longitude =
          Number(
            position.coords.longitude
          );

        const accuracy =
          Number(
            position.coords.accuracy
          );

        if (
          !Number.isFinite(
            latitude
          ) ||
          !Number.isFinite(
            longitude
          )
        ) {
          throw new Error(
            "Invalid GPS coordinates."
          );
        }

        // ----------------------------------------------------
        // GOOGLE MAPS
        // ----------------------------------------------------

        const mapLink =
          `https://www.google.com/maps?q=${latitude},${longitude}`;

        // ----------------------------------------------------
        // GET ADDRESS FROM GPS
        // ----------------------------------------------------

        const locationData =
          await reverseGeocode(
            latitude,
            longitude
          );

        // ----------------------------------------------------
        // FIND NEAREST SHOP
        // ----------------------------------------------------

        const nearestShop =
          findNearestShop(
            shops,
            latitude,
            longitude
          );

        if (!nearestShop) {
          throw new Error(
            "Unable to determine the nearest shop. Please try again."
          );
        }

        // ----------------------------------------------------
        // SAVE GPS
        // ----------------------------------------------------

        setDeliveryLocation({
          lat: latitude,
          lng: longitude,
          accuracy,
          address:
            locationData.address,
          mapLink,
        });

        // ----------------------------------------------------
        // SAVE AUTO ADDRESS
        //
        // This is DISPLAY ONLY.
        // Customer does NOT type this.
        // ----------------------------------------------------

        setForm((previous) => ({
          ...previous,
          address:
            locationData.address,
          pincode:
            locationData.pincode || "",
          map_link: mapLink,
        }));

        // ----------------------------------------------------
        // SAVE NEAREST SHOP
        // ----------------------------------------------------

        setSelectedShop(
          nearestShop
        );

        setShopDistance(
          nearestShop.distance
        );

        toast.success(
          `${nearestShop.name} selected automatically`,
          {
            id: "location-loading",
          }
        );
      } catch (error) {
        console.error(
          "GPS ERROR:",
          error
        );

        let message =
          "Unable to get your current location.";

        if (error?.code === 1) {
          message =
            "Location permission was denied. Please allow location access and try again.";
        } else if (
          error?.code === 2
        ) {
          message =
            "Your location could not be determined. Please turn on Windows/browser location services and try again.";
        } else if (
          error?.code === 3
        ) {
          message =
            "Location request timed out. Please try again.";
        } else if (
          error?.message
        ) {
          message =
            error.message;
        }

        setLocationError(
          message
        );

        setDeliveryLocation(
          null
        );

        setSelectedShop(null);

        setShopDistance(null);

        toast.error(message, {
          id: "location-loading",
        });
      } finally {
        setLocationLoading(false);
      }
    };

  // ==========================================================
  // ORDER TYPE
  // ==========================================================

  const handleOrderTypeChange =
    (type) => {
      setOrderType(type);

      if (
        type === ORDER_TYPES.DINE_IN
      ) {
        setDeliveryLocation(
          null
        );

        setSelectedShop(null);

        setShopDistance(null);

        setLocationError("");

        setForm((previous) => ({
          ...previous,
          address: "",
          landmark: "",
          pincode: "",
          map_link: "",
          delivery_time: "ASAP",
        }));

        setScheduledDate("");
        setScheduledTime("");

        setDinePaymentMethod(
          "CASH"
        );
      }

      if (
        type === ORDER_TYPES.DELIVERY
      ) {
        setSelectedDineShop(
          null
        );

        setTableNo("");

        setDinePaymentMethod(
          "CASH"
        );

        // VERY IMPORTANT:
        // DO NOT call fetchCurrentLocation here.
        //
        // The customer must click the location button.
      }
    };

  // ==========================================================
  // DELIVERY TIME
  // ==========================================================

  const selectDeliveryTime =
    (time) => {
      setForm((previous) => ({
        ...previous,
        delivery_time: time,
      }));

      if (time !== "Schedule") {
        setScheduledDate("");
        setScheduledTime("");
      }
    };

  // ==========================================================
  // PLACE ORDER
  // ==========================================================

  const placeOrder =
    async () => {
      // ------------------------------------------------------
      // COMMON VALIDATION
      // ------------------------------------------------------

      if (
        !form.name.trim() ||
        !form.phone.trim()
      ) {
        toast.error(
          "Please enter your name and phone number."
        );
        return;
      }

      if (!orderType) {
        toast.error(
          "Please select Dine-In or Delivery."
        );
        return;
      }

      // ------------------------------------------------------
      // DINE-IN
      // ------------------------------------------------------

      if (
        orderType ===
        ORDER_TYPES.DINE_IN
      ) {
        if (!selectedDineShop) {
          toast.error(
            "Please select a shop."
          );
          return;
        }

        if (!tableNo) {
          toast.error(
            "Please select your table."
          );
          return;
        }
      }

      // ------------------------------------------------------
      // DELIVERY
      // ------------------------------------------------------

      if (
        orderType ===
        ORDER_TYPES.DELIVERY
      ) {
        if (locationLoading) {
          toast.error(
            "Please wait for location detection to finish."
          );
          return;
        }

        if (!deliveryLocation) {
          toast.error(
            "Please click 'Use My Current Location' first."
          );
          return;
        }

        if (!selectedShop) {
          toast.error(
            "Unable to determine the nearest shop."
          );
          return;
        }

        if (
          shopDistance === null ||
          !Number.isFinite(Number(shopDistance))
        ) {
          toast.error(
            "Unable to determine delivery distance."
          );
          return;
        }

        if (Number(shopDistance) > 10) {
          toast.error(
            `Sorry, we currently deliver only within 10 km. Your location is ${Number(
              shopDistance
            ).toFixed(2)} km away.`
          );
          return;
        }
        if (
          form.delivery_time ===
          "Schedule"
        ) {
          if (
            !scheduledDate ||
            !scheduledTime
          ) {
            toast.error(
              "Please select delivery date and time."
            );
            return;
          }
        }
      }

      // ------------------------------------------------------
      // DELIVERY TIME
      // ------------------------------------------------------

      let finalDeliveryTime =
        form.delivery_time;

      if (
        orderType ===
          ORDER_TYPES.DELIVERY &&
        form.delivery_time ===
          "Schedule"
      ) {
        const dateObject =
          new Date(
            `${scheduledDate}T${scheduledTime}`
          );

        finalDeliveryTime =
          dateObject.toLocaleString(
            "en-IN",
            {
              dateStyle: "medium",
              timeStyle: "short",
            }
          );
      }

      setPlacing(true);

      try {
        // ----------------------------------------------------
        // FINAL PRICING
        // ----------------------------------------------------

        const finalSubtotal =
          Number(subtotal);

        const finalDiscount =
          Number(discount);

        const finalAfterDiscount =
          Math.max(
            0,
            finalSubtotal -
              finalDiscount
          );

        const finalGst =
          Math.round(
            (finalAfterDiscount *
              gstPct) /
              100
          );

        const finalDelivery =
          orderType ===
          ORDER_TYPES.DINE_IN
            ? 0
            : finalSubtotal >=
              Number(
                freeDeliveryAbove
              )
            ? 0
            : Number(
                normalDeliveryCharge
              );

        const finalTotal =
          Math.max(
            0,
            finalAfterDiscount +
              finalGst +
              finalDelivery
          );

        // ----------------------------------------------------
        // SHOP
        // ----------------------------------------------------

        const shop =
          orderType ===
          ORDER_TYPES.DINE_IN
            ? selectedDineShop
            : selectedShop;

        // ----------------------------------------------------
        // PAYMENT
        // ----------------------------------------------------

        const paymentMethod =
          orderType ===
          ORDER_TYPES.DINE_IN
            ? dinePaymentMethod ==="QR"
              ? "RAZORPAY"
              : "CASH"
            : "RAZORPAY";  
        // ----------------------------------------------------
        // GPS
        // ----------------------------------------------------

        const latitude =
          orderType ===
          ORDER_TYPES.DELIVERY
            ? Number(
                deliveryLocation.lat
              )
            : null;

        const longitude =
          orderType ===
          ORDER_TYPES.DELIVERY
            ? Number(
                deliveryLocation.lng
              )
            : null;

        // ----------------------------------------------------
        // ORDER PAYLOAD
        // ----------------------------------------------------

        const payload = {
          items: items.map(
            (item) => ({
              kind: item.kind,
              name: item.name,
              price: Number(
                item.price
              ),
              quantity: Number(
                item.quantity
              ),
              options:
                item.options || {},
            })
          ),

          order_type:
            orderType,

          delivery: {
            name:
              form.name.trim(),

            phone:
              form.phone.trim(),

            // IMPORTANT:
            // This is automatically obtained from GPS.
            address:
              orderType ===
              ORDER_TYPES.DELIVERY
                ? form.address.trim()
                : "",

            landmark:
              orderType ===
              ORDER_TYPES.DELIVERY
                ? form.landmark.trim()
                : "",

            pincode:
              orderType ===
              ORDER_TYPES.DELIVERY
                ? form.pincode.trim()
                : "",

            map_link:
              orderType ===
              ORDER_TYPES.DELIVERY
                ? form.map_link
                : "",

            delivery_time:
              orderType ===
              ORDER_TYPES.DELIVERY
                ? finalDeliveryTime
                : "DINE-IN",

            // REAL CUSTOMER GPS
            latitude,
            longitude,
          },

          // --------------------------------------------------
          // SHOP
          // --------------------------------------------------

          shop_id:
            shop?.id || null,

          shop_name:
            shop?.name ||
            shop?.shortName ||
            null,

          shop_address:
            shop?.address || null,

          shop_distance_km:
            orderType ===
              ORDER_TYPES.DELIVERY &&
            shopDistance !== null
              ? Number(
                  shopDistance.toFixed(
                    2
                  )
                )
              : orderType ===
                ORDER_TYPES.DINE_IN
              ? 0
              : null,

          // --------------------------------------------------
          // TABLE
          // --------------------------------------------------

          table_no:
            orderType ===
            ORDER_TYPES.DINE_IN
              ? tableNo
              : null,

          // --------------------------------------------------
          // PRICING
          // --------------------------------------------------

          subtotal:
            finalSubtotal,

          discount:
            finalDiscount,

          gst:
            Number(finalGst),

          delivery_charge:
            Number(finalDelivery),

          total:
            Number(finalTotal),

          // --------------------------------------------------
          // OFFER / COUPON
          // --------------------------------------------------

          coupon_code:
            couponCode,

          offer_id:
            offerId,

          offer_title:
            offerTitle,

          offer_discount:
            Number(
              offerDiscount
            ),

          // --------------------------------------------------
          // PAYMENT
          // --------------------------------------------------

          payment_method:
            paymentMethod,

          payment_status:
            "pending",
        };

        console.log(
          "ORDER PAYLOAD:",
          payload
        );

        // ----------------------------------------------------
        // SEND TO BACKEND
        // ----------------------------------------------------

        const response =
          await api.post(
            "/orders",
            payload
          );

        const createdOrder = response.data;
        const ourOrderId = createdOrder?.id;
        const orderCode = createdOrder?.order_code;

        if (!ourOrderId) {
          throw new Error(
            "Backend did not return an order ID."
          );
        }

        // ----------------------------------------------------
        // DINE-IN CASH
        // ----------------------------------------------------

        if (
          orderType === ORDER_TYPES.DINE_IN &&
          dinePaymentMethod === "CASH"
        ) {
          sessionStorage.removeItem(
            "icp_cart_pricing"
          );

          clear();

          toast.success(
            "Dine-in order placed successfully!"
          );

          nav(
            `/track/${orderCode}`
          );

          return;
        }



        // ----------------------------------------------------
        // DELIVERY = RAZORPAY
        // ----------------------------------------------------

        const razorpayLoaded =
          await loadRazorpay();

        if (!razorpayLoaded) {
          throw new Error(
            "Unable to load Razorpay. Please check your internet connection and try again."
          );
        }

        // Create Razorpay order on the backend.
        const razorpayResponse =
          await api.post(
            `/orders/${ourOrderId}/razorpay/create`
          );

        const razorpayData =
          razorpayResponse.data;

        if (
          !razorpayData?.key_id ||
          !razorpayData?.razorpay_order_id
        ) {
          throw new Error(
            "Invalid Razorpay response from server."
          );
        }

        // ----------------------------------------------------
        // OPEN RAZORPAY CHECKOUT
        // ----------------------------------------------------

        const razorpayOptions = {
          key:
            razorpayData.key_id,

          amount:
            razorpayData.amount,

          currency:
            razorpayData.currency ||
            "INR",

          name:
            "Ice Cream Paradise",

          description:
            `Order ${orderCode || ""}`,

          order_id:
            razorpayData.razorpay_order_id,

          prefill: {
            name:
              form.name.trim(),

            contact:
              form.phone.trim(),
          },

          notes: {
            order_code:
              orderCode || "",
          },

          theme: {
            color:
              "#1F5F5C",
          },

          modal: {
            ondismiss: () => {
              setPlacing(false);

              toast.error(
                "Payment cancelled. Your order is still pending payment."
              );
            },
          },

          handler:
            async function (
              paymentResponse
            ) {
              try {
                setPlacing(true);

                // Verify the payment signature on YOUR backend.
                const verifyResponse =
                  await api.post(
                    `/orders/${ourOrderId}/razorpay/verify`,
                    {
                      razorpay_order_id:
                        paymentResponse.razorpay_order_id,

                      razorpay_payment_id:
                        paymentResponse.razorpay_payment_id,

                      razorpay_signature:
                        paymentResponse.razorpay_signature,
                    }
                  );

                if (
                  !verifyResponse.data
                    ?.success
                ) {
                  throw new Error(
                    "Payment verification failed."
                  );
                }

                // Clear cart ONLY after successful verification.
                sessionStorage.removeItem(
                  "icp_cart_pricing"
                );

                clear();
                
                toast.success("Payment successful! Order confirmed.");

                nav(`/track/${orderCode}`);
                setPaymentSuccess({
                  transactionId:
                    paymentResponse.razorpay_payment_id,

                  razorpayOrderId:
                    paymentResponse.razorpay_order_id,

                  orderCode: orderCode,

                  amount:
                    Number(razorpayData.amount) / 100,
                });
              } catch (verifyError) {
                console.error(
                  "RAZORPAY VERIFICATION ERROR:",
                  verifyError
                );

                toast.error(
                  verifyError?.response
                    ?.data?.detail ||
                    "Payment verification failed. Please contact support with your order number."
                );
              } finally {
                setPlacing(false);
              }
            },
        };

        const razorpay =
          new window.Razorpay(
            razorpayOptions
          );

        razorpay.on(
          "payment.failed",
          (failureResponse) => {
            console.error(
              "RAZORPAY PAYMENT FAILED:",
              failureResponse
            );

            setPlacing(false);

            toast.error(
              failureResponse
                ?.error?.description ||
                "Payment failed. Please try again."
            );
          }
        );

        razorpay.open();

        // Do NOT clear the cart here.
        // It is cleared only after backend verification.

      } catch (error) {
        console.error(
          "ORDER ERROR:",
          error
        );

        toast.error(
          error?.response?.data
            ?.detail ||
            "Failed to place order."
        );
      } finally {
        setPlacing(false);
      }
    };

  // ==========================================================
  // EMPTY CART
  // ==========================================================

  if (!items.length) {
    return (
      <div className="max-w-4xl mx-auto py-24 px-6 text-center">
        <h1 className="font-heading text-4xl">
          Your cart is empty
        </h1>
      </div>
    );
  }

  

  // ==========================================================
  // RENDER
  // ==========================================================
  if (paymentSuccess) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="relative w-full max-w-2xl">

        {/* Moving coins */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
          <span className="payment-coin coin-1">🪙</span>
          <span className="payment-coin coin-2">🪙</span>
          <span className="payment-coin coin-3">🪙</span>
          <span className="payment-coin coin-4">🪙</span>
          <span className="payment-coin coin-5">🪙</span>
        </div>

        <div className="relative glass rounded-[2rem] p-8 md:p-12 text-center">

          {/* Success icon */}
          <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-[#B8E14A] flex items-center justify-center text-[#1F5F5C] text-4xl shadow-xl">
            ✓
          </div>

          <div className="text-xs uppercase tracking-[0.3em] text-[#B8E14A]">
            Payment Complete
          </div>

          <h1 className="font-heading text-4xl md:text-5xl mt-3">
            Payment Successful!
          </h1>

          <p className="text-white/60 mt-3">
            Thank you! Your order has been confirmed.
          </p>

          {/* Payment details */}
          <div className="mt-8 rounded-3xl bg-white/5 border border-white/10 p-6 text-left space-y-5">

            <div className="flex justify-between gap-4">
              <span className="text-white/50">
                Transaction ID
              </span>

              <span className="font-mono text-sm text-[#B8E14A] break-all text-right">
                {paymentSuccess.transactionId}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-white/50">
                Order ID
              </span>

              <span className="font-semibold">
                {paymentSuccess.orderCode}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-white/50">
                Amount Paid
              </span>

              <span className="font-semibold text-lg">
                ₹{paymentSuccess.amount.toFixed(2)}
              </span>
            </div>

          </div>

          {/* Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">

            <button
              type="button"
              onClick={() =>
                nav(`/track/${paymentSuccess.orderCode}`)
              }
              className="btn-gold justify-center"
            >
              Track Your Order
            </button>

            <button
              type="button"
              onClick={() => nav("/")}
              className="px-6 py-3 rounded-full border border-white/20 hover:bg-white/10 transition"
            >
              Back to Home
            </button>

          </div>

          <p className="text-xs text-white/40 mt-6">
            Please keep your Transaction ID for your records.
          </p>

        </div>
      </div>
    </div>
  );
}
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-12">

      {/* ====================================================
          TITLE
      ==================================================== */}

      <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl mb-10">
        Checkout
      </h1>

      {/* ====================================================
          ORDER TYPE
      ==================================================== */}

      <div className="glass rounded-3xl p-6 md:p-8 mb-8">

        <h3 className="font-heading text-2xl mb-2">
          How would you like your order?
        </h3>

        <p className="text-sm text-[#B7D8D5] mb-6">
          Choose whether you are eating at our
          shop or want the order delivered.
        </p>

        <div className="grid md:grid-cols-2 gap-4">

          {/* DINE-IN */}

          <button
            type="button"
            onClick={() =>
              handleOrderTypeChange(
                ORDER_TYPES.DINE_IN
              )
            }
            className={`rounded-3xl p-6 text-left border-2 transition ${
              orderType ===
              ORDER_TYPES.DINE_IN
                ? "border-[#B8E14A] bg-[#B8E14A]/15"
                : "border-[rgba(184,225,74,0.25)]"
            }`}
          >
            <Utensils className="w-8 h-8 mb-3 text-[#B8E14A]" />

            <div className="font-heading text-xl">
              Dine-In
            </div>

            <div className="text-xs text-[#B7D8D5] mt-1">
              I'm already at the shop
            </div>

            {orderType ===
              ORDER_TYPES.DINE_IN && (
              <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
                ✓ Dine-In selected
              </div>
            )}
          </button>

          {/* DELIVERY */}

          <button
            type="button"
            onClick={() =>
              handleOrderTypeChange(
                ORDER_TYPES.DELIVERY
              )
            }
            className={`rounded-3xl p-6 text-left border-2 transition ${
              orderType ===
              ORDER_TYPES.DELIVERY
                ? "border-[#B8E14A] bg-[#B8E14A]/15"
                : "border-[rgba(184,225,74,0.25)]"
            }`}
          >
            <Truck className="w-8 h-8 mb-3 text-[#B8E14A]" />

            <div className="font-heading text-xl">
              Delivery
            </div>

            <div className="text-xs text-[#B7D8D5] mt-1">
              Use my current location
            </div>

            {orderType ===
              ORDER_TYPES.DELIVERY && (
              <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
                ✓ Delivery selected
              </div>
            )}
          </button>

        </div>
      </div>

      {/* ====================================================
          DINE-IN
      ==================================================== */}

      {orderType ===
        ORDER_TYPES.DINE_IN && (
        <motion.div
          initial={{
            opacity: 0,
            y: 10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="glass rounded-3xl p-6 md:p-8 mb-8"
        >

          <h3 className="font-heading text-2xl mb-2">
            Select Shop & Table
          </h3>

          <p className="text-sm text-[#B7D8D5] mb-6">
            Select where you are currently
            sitting.
          </p>

          <div className="grid md:grid-cols-2 gap-4">

            {shops.map((shop) => (
              <button
                type="button"
                key={shop.id}
                onClick={() => {
                  setSelectedDineShop(
                    shop
                  );
                  setTableNo("");
                }}
                className={`rounded-2xl p-5 text-left border-2 transition ${
                  selectedDineShop?.id ===
                  shop.id
                    ? "border-[#B8E14A] bg-[#B8E14A]/15"
                    : "border-[rgba(184,225,74,0.25)]"
                }`}
              >
                <MapPin className="w-5 h-5 text-[#B8E14A] mb-2" />

                <div className="font-semibold">
                  {shop.name ||
                    shop.shortName}
                </div>

                <div className="text-xs text-[#B7D8D5] mt-1">
                  {shop.address}
                </div>

                {selectedDineShop?.id ===
                  shop.id && (
                  <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
                    ✓ Shop selected
                  </div>
                )}
              </button>
            ))}

          </div>

          {selectedDineShop && (
            <div className="mt-6">

              <label className="block text-xs uppercase tracking-widest text-[#B7D8D5] mb-3">
                Select Table Number
              </label>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">

                {TABLES.map(
                  (table) => (
                    <button
                      type="button"
                      key={table}
                      onClick={() =>
                        setTableNo(
                          table
                        )
                      }
                      className={`rounded-2xl px-4 py-4 border-2 font-semibold transition ${
                        tableNo ===
                        table
                          ? "bg-[#B8E14A] text-[#1F5F5C] border-[#B8E14A]"
                          : "border-[rgba(184,225,74,0.25)]"
                      }`}
                    >
                      {table}
                    </button>
                  )
                )}

              </div>

            </div>
          )}

        </motion.div>
      )}

      {/* ====================================================
          MAIN
      ==================================================== */}

      <div className="grid lg:grid-cols-3 gap-8">

        {/* ==================================================
            LEFT
        ================================================== */}

        <div className="lg:col-span-2 space-y-8">

          {/* CUSTOMER DETAILS */}

          <div className="glass rounded-3xl p-6 md:p-8">

            <h3 className="font-heading text-2xl mb-6">
              {orderType ===
              ORDER_TYPES.DELIVERY
                ? "Delivery Details"
                : "Customer Details"}
            </h3>

            <div className="grid md:grid-cols-2 gap-4">

              <Field
                label="Full name"
                value={form.name}
                testId="chk-name"
                onChange={(value) =>
                  setForm({
                    ...form,
                    name: value,
                  })
                }
              />

              <Field
                label="Phone number"
                value={form.phone}
                testId="chk-phone"
                type="tel"
                onChange={(value) =>
                  setForm({
                    ...form,
                    phone: value,
                  })
                }
              />

            </div>

            {/* =================================================
                DELIVERY LOCATION
            ================================================= */}

            {orderType ===
              ORDER_TYPES.DELIVERY && (
              <div className="mt-6">

                <div className="rounded-3xl border-2 border-[#B8E14A]/40 bg-[#B8E14A]/10 p-6">

                  <div className="flex items-center gap-3 mb-5">

                    <div className="w-11 h-11 rounded-full bg-[#B8E14A]/20 flex items-center justify-center">

                      {locationLoading ? (
                        <Loader2 className="w-6 h-6 text-[#B8E14A] animate-spin" />
                      ) : (
                        <LocateFixed className="w-6 h-6 text-[#B8E14A]" />
                      )}

                    </div>

                    <div>
                      <div className="font-heading text-xl">
                        Your Current Location
                      </div>

                      <div className="text-xs text-[#B7D8D5] mt-1">
                        Your GPS location is used
                        automatically for delivery.
                      </div>
                    </div>

                  </div>

                  {/* BEFORE GPS */}

                  {!locationLoading &&
                    !deliveryLocation && (
                    <div>

                      <button
                        type="button"
                        onClick={
                          fetchCurrentLocation
                        }
                        disabled={
                          shopsLoading
                        }
                        className="btn-primary"
                      >
                        {shopsLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading shops...
                          </>
                        ) : (
                          <>
                            <LocateFixed className="w-5 h-5" />
                            Use My Current Location
                          </>
                        )}
                      </button>

                      <p className="text-xs text-[#B7D8D5] mt-3">
                        We will get your current
                        GPS position only after you
                        click this button.
                      </p>

                    </div>
                  )}

                  {/* GPS LOADING */}

                  {locationLoading && (
                    <div className="flex items-center gap-3 text-[#B8E14A]">

                      <Loader2 className="w-5 h-5 animate-spin" />

                      <span>
                        Detecting your current
                        location...
                      </span>

                    </div>
                  )}

                  {/* ERROR */}

                  {locationError && (
                    <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">

                      {locationError}

                      <button
                        type="button"
                        onClick={
                          fetchCurrentLocation
                        }
                        className="flex items-center gap-2 mt-3 font-semibold underline"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Try Again
                      </button>

                    </div>
                  )}

                  {/* LOCATION SUCCESS */}

                  {deliveryLocation &&
                    !locationLoading && (
                    <div className="space-y-4">

                      {/* ADDRESS */}

                      <div className="rounded-2xl bg-white p-4">

                        <div className="text-xs uppercase tracking-widest text-[#1F5F5C]/60 mb-2">
                          Detected Address
                        </div>

                        <div className="text-sm text-[#1F5F5C]">
                          {form.address}
                        </div>

                        <div className="text-xs text-[#1F5F5C]/50 mt-2">
                          This address is automatically
                          obtained from your GPS.
                          You do not need to type it.
                        </div>

                      </div>

                      {/* GPS */}

                      <div className="grid sm:grid-cols-2 gap-4">

                        <div className="rounded-2xl bg-white p-4">

                          <div className="text-xs text-[#1F5F5C]/60">
                            GPS Coordinates
                          </div>

                          <div className="font-semibold text-[#1F5F5C] mt-1">
                            {deliveryLocation.lat.toFixed(
                              6
                            )}
                            {" , "}
                            {deliveryLocation.lng.toFixed(
                              6
                            )}
                          </div>

                        </div>

                        <div className="rounded-2xl bg-white p-4">

                          <div className="text-xs text-[#1F5F5C]/60">
                            GPS Accuracy
                          </div>

                          <div className="font-semibold text-[#1F5F5C] mt-1">
                            {Number(
                              deliveryLocation.accuracy
                            ).toFixed(0)}{" "}
                            meters
                          </div>

                        </div>

                      </div>

                      {/* PINCODE */}

                      <div className="rounded-2xl bg-white p-4">

                        <div className="text-xs text-[#1F5F5C]/60">
                          Pincode
                        </div>

                        <div className="font-semibold text-[#1F5F5C] mt-1">
                          {form.pincode ||
                            "Not available"}
                        </div>

                      </div>

                      {/* MAP */}

                      <a
                        href={
                          deliveryLocation.mapLink
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F5F5C] underline"
                      >
                        <Navigation className="w-4 h-4" />
                        View my location on Google
                        Maps
                      </a>

                      {/* REFRESH */}

                      <button
                        type="button"
                        onClick={
                          fetchCurrentLocation
                        }
                        className="flex items-center gap-2 text-sm font-semibold text-[#1F5F5C] underline"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh current location
                      </button>

                    </div>
                  )}

                </div>

                {/* LANDMARK */}

                {deliveryLocation && (
                  <div className="grid md:grid-cols-2 gap-4 mt-4">

                    <Field
                      label="Landmark (optional)"
                      value={form.landmark}
                      onChange={(value) =>
                        setForm({
                          ...form,
                          landmark: value,
                        })
                      }
                    />

                    <div className="rounded-2xl border border-[rgba(184,225,74,0.35)] bg-[#B8E14A]/10 p-4">

                      <div className="text-xs uppercase tracking-widest text-[#B7D8D5]">
                        Pincode
                      </div>

                      <div className="font-semibold mt-2">
                        {form.pincode ||
                          "Automatically detected"}
                      </div>

                    </div>

                  </div>
                )}

              </div>
            )}

          </div>

          {/* =================================================
              NEAREST SHOP
          ================================================= */}

          {orderType ===
            ORDER_TYPES.DELIVERY &&
            deliveryLocation && (
            <div className="glass rounded-3xl p-6 md:p-8">

              <h3 className="font-heading text-2xl mb-5">
                Nearest Shop
              </h3>

              {selectedShop ? (
                <div className="rounded-3xl border-2 border-[#B8E14A]/40 bg-[#B8E14A]/10 p-6">

                  <div className="flex items-start gap-4">

                    <div className="w-12 h-12 rounded-full bg-[#B8E14A]/20 flex items-center justify-center">

                      <MapPin className="w-6 h-6 text-[#B8E14A]" />

                    </div>

                    <div className="flex-1">

                      <div className="text-xl font-heading">
                        {selectedShop.name ||
                          selectedShop.shortName}
                      </div>

                      <div className="text-sm text-[#B7D8D5] mt-2">
                        {selectedShop.address}
                      </div>

                      {shopDistance !== null &&
                        Number(shopDistance) > 10 && (
                          <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 font-semibold">
                            Sorry, we currently deliver only within 10 km.
                            Your location is {shopDistance.toFixed(2)} km away.
                          </div>
                        )}

                      <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
                        ✓ Automatically selected from
                        your GPS location
                      </div>

                      <div className="text-xs text-[#B7D8D5] mt-2">
                        ✓ Customer cannot manually
                        change the delivery shop
                      </div>

                    </div>

                  </div>

                </div>
              ) : (
                <div className="text-sm text-[#B7D8D5]">
                  Calculating nearest shop...
                </div>
              )}

            </div>
          )}

          {/* =================================================
              DELIVERY TIME
          ================================================= */}

          {orderType ===
            ORDER_TYPES.DELIVERY && (
            <div className="glass rounded-3xl p-6 md:p-8">

              <h3 className="font-heading text-2xl mb-5">
                Delivery Time
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

                {DELIVERY_TIMES.map(
                  (time) => (
                    <button
                      type="button"
                      key={time}
                      onClick={() =>
                        selectDeliveryTime(
                          time
                        )
                      }
                      className={`rounded-2xl px-4 py-4 border-2 font-semibold ${
                        form.delivery_time ===
                        time
                          ? "bg-[#B8E14A] text-[#1F5F5C] border-[#B8E14A]"
                          : "border-[rgba(184,225,74,0.25)]"
                      }`}
                    >
                      {time}
                    </button>
                  )
                )}

              </div>

              {form.delivery_time ===
                "Schedule" && (
                <div className="grid md:grid-cols-2 gap-4 mt-4">

                  <Field
                    label="Date"
                    type="date"
                    value={
                      scheduledDate
                    }
                    onChange={
                      setScheduledDate
                    }
                  />

                  <Field
                    label="Time"
                    type="time"
                    value={
                      scheduledTime
                    }
                    onChange={
                      setScheduledTime
                    }
                  />

                </div>
              )}

            </div>
          )}

          {/* =================================================
              PAYMENT
          ================================================= */}

          <div className="glass rounded-3xl p-6 md:p-8">

            <h3 className="font-heading text-2xl mb-5">
              Payment
            </h3>

            {orderType ===
              ORDER_TYPES.DINE_IN ? (
              <div className="grid md:grid-cols-2 gap-4">

                <PaymentButton
                  active={
                    dinePaymentMethod ===
                    "CASH"
                  }
                  onClick={() =>
                    setDinePaymentMethod(
                      "CASH"
                    )
                  }
                  icon={
                    <Banknote className="w-6 h-6 text-[#B8E14A]" />
                  }
                  title="Cash"
                  subtitle="Pay at the shop"
                />

                <PaymentButton
                  active={
                    dinePaymentMethod ===
                    "QR"
                  }
                  onClick={() =>
                    setDinePaymentMethod(
                      "QR"
                    )
                  }
                  icon={
                    <Banknote className="w-6 h-6 text-[#B8E14A]" />
                  }
                  title="UPI / Razorpay"
                  subtitle="Pay securely using UPI, PhonePe, Google Pay, cards and more"
                />

              </div>
            ) : (
              <div className="rounded-3xl border-2 border-[#B8E14A]/40 bg-[#B8E14A]/10 p-6">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-full bg-[#B8E14A]/20 flex items-center justify-center">
                    <Banknote className="w-6 h-6 text-[#B8E14A]" />
                  </div>

                  <div>
                    <div className="font-heading text-xl">
                      Secure Razorpay Payment
                    </div>

                    <div className="text-sm text-[#B7D8D5] mt-1">
                      Delivery orders are paid securely
                      through Razorpay using UPI, PhonePe,
                      Google Pay, Paytm, cards and other
                      available payment methods.
                    </div>

                    <div className="text-xs text-[#B8E14A] mt-3 font-semibold">
                      ✓ Razorpay checkout opens after you click
                      Place Delivery Order
                    </div>
                  </div>

                </div>

              </div>
            )}

          </div>

        </div>

        {/* ==================================================
            RIGHT - SUMMARY
        ================================================== */}

        <div>

          <div className="glass-dark rounded-3xl p-6 md:p-8 sticky top-24">

            <h3 className="font-heading text-2xl mb-6">
              Order Summary
            </h3>

            <div className="space-y-4 mb-6">

              {items.map(
                (item, index) => (
                  <div
                    key={
                      item.id ||
                      item._id ||
                      index
                    }
                    className="flex items-center justify-between gap-3"
                  >

                    <div className="min-w-0">

                      <div className="font-medium truncate">
                        {item.name}
                      </div>

                      <div className="text-xs text-[#B7D8D5]">
                        ×{" "}
                        {item.quantity}
                      </div>

                    </div>

                    <div className="font-semibold text-[#B8E14A]">
                      ₹
                      {Number(
                        item.price *
                          item.quantity
                      ).toFixed(0)}
                    </div>

                  </div>
                )
              )}

            </div>

            {/* OFFER */}

            {offerTitle && (
              <div className="rounded-2xl bg-[#B8E14A]/10 border border-[#B8E14A]/40 p-4 mb-4">

                <div className="flex items-center gap-2 text-sm font-semibold">

                  <Gift className="w-4 h-4 text-[#B8E14A]" />

                  {offerTitle}

                </div>

                <div className="text-xs text-green-700 mt-1">
                  Offer applied successfully
                </div>

              </div>
            )}

            {/* COUPON */}

            {couponCode && (
              <div className="rounded-2xl bg-green-50 border border-green-200 p-4 mb-4">

                <div className="flex items-center gap-2 text-sm font-semibold text-green-700">

                  <Tag className="w-4 h-4" />

                  {couponCode}

                </div>

                <div className="text-xs text-green-700 mt-1">
                  Coupon applied successfully
                </div>

              </div>
            )}

            {/* DINE-IN */}

            {orderType ===
              ORDER_TYPES.DINE_IN &&
              selectedDineShop && (
              <div className="rounded-2xl bg-[#B8E14A]/10 border border-[#B8E14A]/40 p-4 mb-4">

                <div className="flex items-center gap-2 text-sm font-semibold">

                  <Utensils className="w-4 h-4 text-[#B8E14A]" />

                  Dine-In
                </div>

                <div className="text-sm font-medium mt-2">
                  {selectedDineShop.name ||
                    selectedDineShop.shortName}
                </div>

                {tableNo && (
                  <div className="text-xs text-[#B7D8D5] mt-1">
                    🪑 {tableNo}
                  </div>
                )}

              </div>
            )}

            {/* DELIVERY */}

            {orderType ===
              ORDER_TYPES.DELIVERY &&
              selectedShop && (
              <div className="rounded-2xl bg-[#B8E14A]/10 border border-[#B8E14A]/40 p-4 mb-4">

                <div className="flex items-center gap-2 text-sm font-semibold">

                  <Truck className="w-4 h-4 text-[#B8E14A]" />

                  Delivery
                </div>

                <div className="text-sm font-medium mt-2">
                  Automatically assigned:
                </div>

                <div className="text-lg font-heading mt-1">
                  {selectedShop.name ||
                    selectedShop.shortName}
                </div>

                {shopDistance !==
                  null && (
                  <div className="text-xs text-[#B8E14A] mt-1 font-semibold">
                    {shopDistance.toFixed(
                      2
                    )}{" "}
                    km away
                  </div>
                )}

                {deliveryLocation && (
                  <div className="text-xs text-[#B7D8D5] mt-3">
                    📍{" "}
                    {deliveryLocation.lat.toFixed(
                      6
                    )}
                    {" , "}
                    {deliveryLocation.lng.toFixed(
                      6
                    )}
                  </div>
                )}

                <div className="text-xs text-[#B8E14A] mt-3">
                  ✓ Nearest shop selected
                  automatically
                </div>

              </div>
            )}

            {/* PRICES */}

            <div className="space-y-2 text-sm border-t border-[rgba(184,225,74,0.35)] pt-4">

              <Row
                label="Subtotal"
                value={`₹${Number(
                  subtotal
                ).toFixed(0)}`}
              />

              {discount > 0 && (
                <Row
                  label="Discount"
                  value={`- ₹${Number(
                    discount
                  ).toFixed(0)}`}
                  accent
                />
              )}

              <Row
                label={`GST (${gstPct}%)`}
                value={`₹${gst}`}
              />

              <Row
                label="Delivery"
                value={
                  deliveryCharge ===
                  0
                    ? "FREE"
                    : `₹${deliveryCharge}`
                }
              />

            </div>

            {orderType ===
              ORDER_TYPES.DELIVERY &&
              deliveryCharge === 0 &&
              Number(subtotal) >=
                Number(
                  freeDeliveryAbove
                ) && (
              <div className="mt-3 rounded-xl bg-green-50 border border-green-200 p-3 text-xs text-green-700 font-medium">
                🎉 Free delivery unlocked!
              </div>
            )}

            {/* TOTAL */}

            <div className="border-t border-[rgba(184,225,74,0.35)] mt-4 pt-4 flex items-center justify-between">

              <span className="uppercase text-xs tracking-widest text-[#B7D8D5]">
                Total
              </span>

              <span className="font-heading text-3xl text-[#B8E14A]">
                ₹{total}
              </span>

            </div>

            {/* PLACE ORDER */}

            <button
              type="button"
              onClick={
                placeOrder
              }
              disabled={
                placing ||
                !orderType ||
                (
                  orderType ===
                    ORDER_TYPES.DINE_IN &&
                  (
                    !selectedDineShop ||
                    !tableNo
                  )
                ) ||
                (
                  orderType ===
                    ORDER_TYPES.DELIVERY &&
                  (
                    locationLoading ||
                    !deliveryLocation ||
                    !selectedShop||
                    shopDistance === null ||
                    Number(shopDistance) > 10
                  )
                )
              }
              className="btn-primary w-full justify-center mt-5 disabled:opacity-50"
            >

              {placing
                ? "Placing..."
                : !orderType
                ? "Select Order Type"
                : orderType ===
                  ORDER_TYPES.DINE_IN
                ? !selectedDineShop
                  ? "Select Shop"
                  : !tableNo
                  ? "Select Table"
                  : "Place Dine-In Order"
                : locationLoading
                ? "Detecting Location..."
                : !deliveryLocation
                ? "Use My Current Location"
                : !selectedShop
                ? "Finding Nearest Shop..."
                : "Place Delivery Order"}

            </button>

            <div className="text-xs text-[#B7D8D5] mt-4 text-center">

              {orderType ===
              ORDER_TYPES.DINE_IN
                ? "Your order will be prepared at your selected shop and table."
                : "Your GPS location is used automatically. The nearest shop is assigned and the order is sent to its kitchen."}

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}